import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../api'
import { calculateRawMaterialUnitConsumption, isSemiFinishedEligibleForRecipeReport } from '../costUtils'
import { Category, Product, RawMaterial, RawMaterialCostHistory, SemiFinishedProduct } from '../types'

interface Props {
  rawMaterial: RawMaterial
  products: Product[]
  semiFinished: SemiFinishedProduct[]
  categories: Category[]
  productsMap: Map<string, Product>
  semiFinishedMap: Map<string, SemiFinishedProduct>
  onBack: () => void
  onOpenReport: (item: Product | SemiFinishedProduct, type: 'product' | 'semi_finished') => void
}

interface ReportRow {
  id: string
  code: string
  name: string
  categoryName: string
  categoryId?: string | null
  type: 'product' | 'semi_finished'
  typeLabel: 'Mamul' | 'Yarı Mamul'
  unitAmount: number
  entryUnit: string
  rawItem: Product | SemiFinishedProduct
}

interface ColumnDef {
  key: string
  label: string
}

function formatNumber(val: number, maxDecimals: number = 4): string {
  if (isNaN(val) || !isFinite(val)) return '0'
  return val.toLocaleString('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  })
}

function formatCurrency(val: number): string {
  if (isNaN(val) || !isFinite(val)) return '0,00'
  return val.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatDateTime(dateStr: string): string {
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return dateStr
    return d.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return dateStr
  }
}

function ArrowLeftIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }}
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  )
}

function HistoryIcon({ size = 15, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px', ...style }}
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )
}

function PieChartIcon({ size = 15, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px', ...style }}
    >
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      <path d="M22 12A10 10 0 0 0 12 2v10z" />
    </svg>
  )
}

function GearIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
}

export function RawMaterialReportView({
  rawMaterial,
  products,
  semiFinished,
  categories,
  productsMap,
  semiFinishedMap,
  onBack,
  onOpenReport,
}: Props) {
  const [activeTab, setActiveTab] = useState<'usage' | 'cost_history'>('usage')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedType, setSelectedType] = useState<'' | 'product' | 'semi_finished'>('')
  const [showColumnModal, setShowColumnModal] = useState<boolean>(false)
  const [hiddenColumns, setHiddenColumns] = useState<Record<string, boolean>>({})

  // Cost History (Price Revisions) state
  const [costHistory, setCostHistory] = useState<RawMaterialCostHistory[]>([])
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false)
  const [historyError, setHistoryError] = useState<string | null>(null)
  const [showHistoryColumnModal, setShowHistoryColumnModal] = useState<boolean>(false)
  const [hiddenHistoryColumns, setHiddenHistoryColumns] = useState<Record<string, boolean>>({})

  const fetchCostHistory = async () => {
    setLoadingHistory(true)
    setHistoryError(null)
    try {
      const data = await api.getRawMaterialCostHistory(rawMaterial.id)
      setCostHistory(data)
    } catch (err: any) {
      setHistoryError(err?.message || 'Fiyat revizyon geçmişi yüklenemedi.')
    } finally {
      setLoadingHistory(false)
    }
  }

  useEffect(() => {
    fetchCostHistory()
  }, [rawMaterial.id])

  const rmUnit = rawMaterial.unit || 'Kg'

  // Recursively inspect all semi-finished and finished products
  const allRows: ReportRow[] = useMemo(() => {
    const rows: ReportRow[] = []

    // 1. Semi-Finished Goods
    for (const sf of semiFinished) {
      const amount = calculateRawMaterialUnitConsumption(sf, rawMaterial.id, semiFinishedMap, productsMap)
      if (amount > 0.000001) {
        rows.push({
          id: sf.id,
          code: sf.code || '',
          name: sf.name || '',
          categoryName: sf.category?.name || '—',
          categoryId: sf.category_id,
          type: 'semi_finished',
          typeLabel: 'Yarı Mamul',
          unitAmount: amount,
          entryUnit: sf.unit || 'Adet',
          rawItem: sf,
        })
      }
    }

    // 2. Finished Products
    for (const prod of products) {
      const amount = calculateRawMaterialUnitConsumption(prod, rawMaterial.id, semiFinishedMap, productsMap)
      if (amount > 0.000001) {
        rows.push({
          id: prod.id,
          code: prod.code || '',
          name: prod.name || '',
          categoryName: prod.category?.name || '—',
          categoryId: prod.category_id,
          type: 'product',
          typeLabel: 'Mamul',
          unitAmount: amount,
          entryUnit: prod.unit || 'Adet',
          rawItem: prod,
        })
      }
    }

    // Default sort by code ascending
    return rows.sort((a, b) => a.code.localeCompare(b.code, 'tr'))
  }, [rawMaterial.id, products, semiFinished, semiFinishedMap, productsMap])

  // Count breakdowns
  const productCount = useMemo(() => allRows.filter((r) => r.type === 'product').length, [allRows])
  const semiFinishedCount = useMemo(() => allRows.filter((r) => r.type === 'semi_finished').length, [allRows])

  // Filtered rows
  const filteredRows = useMemo(() => {
    return allRows.filter((row) => {
      const term = searchTerm.trim().toLowerCase()
      const matchSearch =
        !term ||
        (row.name && row.name.toLowerCase().includes(term)) ||
        (row.code && row.code.toLowerCase().includes(term))
      const matchCat = !selectedCategory || row.categoryId === selectedCategory
      const matchType = !selectedType || row.type === selectedType
      return matchSearch && matchCat && matchType
    })
  }, [allRows, searchTerm, selectedCategory, selectedType])

  // Total unit consumption across filtered items
  const totalUnitAmountFiltered = useMemo(() => {
    return filteredRows.reduce((sum, r) => sum + r.unitAmount, 0)
  }, [filteredRows])

  // Column definitions for customizable visibility
  const columns: ColumnDef[] = useMemo(() => [
    { key: 'code', label: 'Kod' },
    { key: 'name', label: 'Ad' },
    { key: 'category', label: 'Kategori' },
    { key: 'type', label: 'Tür' },
    { key: 'unit_amount', label: 'Birim Miktar' },
  ], [])

  const isColVisible = (key: string) => !hiddenColumns[key]

  const handleToggleColumn = (key: string) => {
    setHiddenColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const handleShowAllColumns = () => {
    setHiddenColumns({})
  }

  const handleHideAllColumns = () => {
    const allHidden: Record<string, boolean> = {}
    columns.forEach((col) => {
      allHidden[col.key] = true
    })
    setHiddenColumns(allHidden)
  }

  // Column definitions for Price Revisions (Fiyat Revizyonları)
  const historyColumns: ColumnDef[] = useMemo(
    () => [
      { key: 'order', label: 'Sıra No' },
      { key: 'date', label: 'Değişiklik Tarihi' },
      { key: 'cost', label: 'Birim Maliyet' },
      { key: 'prev_cost', label: 'Önceki Maliyet' },
      { key: 'diff', label: 'Fark (₺)' },
      { key: 'percent', label: 'Değişim (%)' },
    ],
    []
  )

  const isHistoryColVisible = (key: string) => !hiddenHistoryColumns[key]

  const handleToggleHistoryColumn = (key: string) => {
    setHiddenHistoryColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const handleShowAllHistoryColumns = () => {
    setHiddenHistoryColumns({})
  }

  const handleHideAllHistoryColumns = () => {
    const allHidden: Record<string, boolean> = {}
    historyColumns.forEach((col) => {
      allHidden[col.key] = true
    })
    setHiddenHistoryColumns(allHidden)
  }

  const processedRevisions = useMemo(() => {
    const asc = [...costHistory].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    )
    const mapped = asc.map((rev, idx) => {
      const cost = Number(rev.maliyet)
      const prevCost = idx > 0 ? Number(asc[idx - 1].maliyet) : null
      const diff = prevCost !== null ? cost - prevCost : null
      const percent = prevCost !== null && prevCost > 0 ? ((cost - prevCost) / prevCost) * 100 : null
      return {
        id: rev.id,
        order: idx + 1,
        createdAt: rev.created_at,
        cost,
        prevCost,
        diff,
        percent,
      }
    })
    return mapped.reverse()
  }, [costHistory])

  return (
    <div className="view-container">
      {/* Top Header / Breadcrumb Actions */}
      <div className="section-header-flex" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onBack}
            title="Geri Dön"
          >
            <ArrowLeftIcon />
            Geri Dön
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>{rawMaterial.name}</span>
            </h2>
            <code className="badge badge-secondary" style={{ fontSize: '0.85rem' }}>
              {rawMaterial.code}
            </code>
            <span
              className="badge"
              style={{
                fontSize: '0.82rem',
                backgroundColor: '#10b981',
                color: '#fff',
                fontWeight: 600,
              }}
            >
              Hammadde
            </span>
          </div>
        </div>

        <div className="header-actions">
          <div className="badge badge-outline" style={{ padding: '0.45rem 0.85rem', fontSize: '0.85rem' }}>
            Birim Alış: <strong>₺{formatCurrency(Number(rawMaterial.maliyet || 0))}</strong> / {rmUnit}
          </div>
        </div>
      </div>

      {/* Report Tab Nav (matching ProductReportsView) */}
      <div className="report-tab-nav">
        <button
          type="button"
          className={`report-tab-btn ${activeTab === 'usage' ? 'active' : ''}`}
          onClick={() => setActiveTab('usage')}
        >
          <PieChartIcon />
          Hammadde Raporu
        </button>
        <button
          type="button"
          className={`report-tab-btn ${activeTab === 'cost_history' ? 'active' : ''}`}
          onClick={() => setActiveTab('cost_history')}
        >
          <HistoryIcon />
          Fiyat Revizyonları
        </button>
      </div>

      {activeTab === 'usage' ? (
        <>
          {/* Summary Label Banner */}
          <div className="report-summary-banner">
        <div className="report-summary-text">
          Bu hammadde{' '}
          <strong style={{ color: 'var(--color-primary)' }}>
            {allRows.length} adet
          </strong>{' '}
          mamul ve yarı mamul reçetesinde kullanılmaktadır.
        </div>
        <div className="report-summary-subtext">
          Toplam {productCount} Mamul Ürün • {semiFinishedCount} Yarı Mamul • Kategori: {rawMaterial.category?.name || '—'}
        </div>
      </div>

      {/* Empty State */}
      {allRows.length === 0 ? (
        <div className="card empty-state" style={{ padding: '3.5rem 1.5rem', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '0.95rem' }}>
            Bu hammaddeyi içeren herhangi bir reçete bileşeni bulunmuyor.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onBack}
            style={{ marginTop: '1rem' }}
          >
            Geri Dön
          </button>
        </div>
      ) : (
        <div>
          {/* Filter Bar */}
          <div
            className="filter-bar"
            style={{
              display: 'flex',
              gap: '0.75rem',
              marginTop: '1rem',
              marginBottom: '0.5rem',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <input
              type="text"
              className="form-control"
              placeholder="Ürün adı veya koduna göre ara…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ maxWidth: '280px' }}
            />
            <select
              className="form-control"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{ maxWidth: '200px' }}
            >
              <option value="">Tüm Kategoriler</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              className="form-control"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as any)}
              style={{ maxWidth: '170px' }}
            >
              <option value="">Tüm Türler</option>
              <option value="product">Mamul</option>
              <option value="semi_finished">Yarı Mamul</option>
            </select>
          </div>

          {/* Table Container Styled Like Price List & Product Reports */}
          <div
            className="card table-card"
            style={{
              marginTop: '0.75rem',
              overflowX: 'auto',
              overflowY: 'hidden',
              maxWidth: '100%',
            }}
          >
            <table
              className="data-table price-list-table report-table"
              style={{ width: '100%', minWidth: '780px' }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      width: '38px',
                      minWidth: '38px',
                      textAlign: 'center',
                      verticalAlign: 'bottom',
                      padding: '0.45rem 0.35rem',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setShowColumnModal(true)}
                      title="Sütun Görünürlüğü"
                      aria-label="Sütun görünürlüğünü ayarla"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '3px',
                        borderRadius: '4px',
                        color: '#6b7280',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#111827'
                        e.currentTarget.style.background = '#e5e7eb'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#6b7280'
                        e.currentTarget.style.background = 'transparent'
                      }}
                    >
                      <GearIcon size={14} />
                    </button>
                  </th>
                  {isColVisible('code') && (
                    <th style={{ minWidth: '130px' }}>
                      Bileşen<br />Kodu
                    </th>
                  )}
                  {isColVisible('name') && (
                    <th style={{ minWidth: '220px' }}>
                      Bileşen<br />Adı
                    </th>
                  )}
                  {isColVisible('category') && (
                    <th style={{ minWidth: '160px' }}>
                      Kategori
                    </th>
                  )}
                  {isColVisible('type') && (
                    <th style={{ minWidth: '120px' }}>
                      Tür
                    </th>
                  )}
                  {isColVisible('unit_amount') && (
                    <th style={{ minWidth: '160px', textAlign: 'right' }}>
                      Birim Miktar İçin<br />Sarfiyat
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--color-text-muted, #64748b)' }}
                    >
                      Arama kriterlerine uygun ürün veya yarı mamul bulunamadı.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => (
                    <tr key={`${row.type}-${row.id}`}>
                      <td style={{ textAlign: 'center', padding: '0.35rem 0.25rem', verticalAlign: 'middle' }}>
                        {row.type === 'semi_finished' && isSemiFinishedEligibleForRecipeReport(row.rawItem as SemiFinishedProduct) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => onOpenReport(row.rawItem, row.type)}
                            title="Reçete Raporu"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '3px 5px',
                              lineHeight: 1,
                            }}
                          >
                            <PieChartIcon size={13} style={{ marginRight: 0 }} />
                          </button>
                        )}
                      </td>
                      {isColVisible('code') && (
                        <td>
                          <code className="bom-code-badge">{row.code}</code>
                        </td>
                      )}
                      {isColVisible('name') && (
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                            <span style={{ fontWeight: 500 }}>{row.name}</span>
                          </div>
                        </td>
                      )}
                      {isColVisible('category') && (
                        <td>{row.categoryName}</td>
                      )}
                      {isColVisible('type') && (
                        <td>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: row.type === 'product' ? 'var(--color-primary, #2563eb)' : '#8b5cf6',
                              color: '#fff',
                              fontWeight: 500,
                              fontSize: '0.78rem',
                            }}
                          >
                            {row.typeLabel}
                          </span>
                        </td>
                      )}
                      {isColVisible('unit_amount') && (
                        <td className="tabular-nums" style={{ textAlign: 'right' }}>
                          <strong>{formatNumber(row.unitAmount, 4)}</strong> {rmUnit}
                          <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block' }}>
                            1 {row.entryUnit} için
                          </span>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="report-table-footer">
                  <td style={{ textAlign: 'center', color: '#9ca3af' }}>•</td>
                  {isColVisible('code') && (
                    <td>
                      <strong>Genel Toplam</strong>
                    </td>
                  )}
                  {isColVisible('name') && (
                    <td>
                      {!isColVisible('code') ? <strong>Genel Toplam</strong> : null}
                    </td>
                  )}
                  {isColVisible('category') && <td>—</td>}
                  {isColVisible('type') && (
                    <td>
                      <strong>{filteredRows.length} Kayıt</strong>
                    </td>
                  )}
                  {isColVisible('unit_amount') && (
                    <td className="tabular-nums" style={{ textAlign: 'right' }}>
                      <strong>{formatNumber(totalUnitAmountFiltered, 4)} {rmUnit}</strong>
                    </td>
                  )}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
        </>
      ) : (
        <>
          {/* Fiyat Revizyonları Summary Banner */}
          <div className="report-summary-banner">
            <div className="report-summary-text">
              Bu hammadde için toplam{' '}
              <strong style={{ color: 'var(--color-primary)' }}>
                {costHistory.length} adet
              </strong>{' '}
              fiyat revizyonu kayıtlıdır.
            </div>
            <div className="report-summary-subtext">
              Maksimum son 30 fiyat değişimi saklanır • Geçerli Birim Alış: ₺{formatCurrency(Number(rawMaterial.maliyet || 0))} / {rmUnit} • Kategori: {rawMaterial.category?.name || '—'}
            </div>
          </div>

          {loadingHistory ? (
            <div className="card empty-state" style={{ padding: '3.5rem 1.5rem', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '0.95rem' }}>Fiyat revizyon geçmişi yükleniyor…</p>
            </div>
          ) : historyError ? (
            <div className="card empty-state" style={{ padding: '3.5rem 1.5rem', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '0.95rem', color: 'var(--color-danger, #ef4444)' }}>
                {historyError}
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={fetchCostHistory}
                style={{ marginTop: '1rem' }}
              >
                Yeniden Dene
              </button>
            </div>
          ) : costHistory.length === 0 ? (
            <div className="card empty-state" style={{ padding: '3.5rem 1.5rem', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '0.95rem' }}>
                Bu hammadde için henüz kayıtlı bir fiyat geçmişi bulunmuyor.
              </p>
            </div>
          ) : (
            <div className="table-card" style={{ marginTop: '1rem' }}>
              <table className="data-table price-list-table report-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px', textAlign: 'center', verticalAlign: 'middle', padding: '0.5rem 0.25rem' }}>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => setShowHistoryColumnModal(true)}
                        title="Sütun Görünürlüğü"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#6b7280',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '4px',
                          borderRadius: '4px',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#111827'
                          e.currentTarget.style.background = '#e5e7eb'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#6b7280'
                          e.currentTarget.style.background = 'transparent'
                        }}
                      >
                        <GearIcon size={14} />
                      </button>
                    </th>
                    {isHistoryColVisible('order') && <th style={{ minWidth: '70px', textAlign: 'center' }}>Sıra No</th>}
                    {isHistoryColVisible('date') && <th style={{ minWidth: '180px' }}>Değişiklik Tarihi</th>}
                    {isHistoryColVisible('cost') && <th style={{ minWidth: '150px', textAlign: 'right' }}>Birim Maliyet</th>}
                    {isHistoryColVisible('prev_cost') && <th style={{ minWidth: '150px', textAlign: 'right' }}>Önceki Maliyet</th>}
                    {isHistoryColVisible('diff') && <th style={{ minWidth: '130px', textAlign: 'right' }}>Fark (₺)</th>}
                    {isHistoryColVisible('percent') && <th style={{ minWidth: '130px', textAlign: 'right' }}>Değişim (%)</th>}
                  </tr>
                </thead>
                <tbody>
                  {processedRevisions.map((row) => (
                    <tr key={row.id}>
                      <td style={{ textAlign: 'center', color: '#9ca3af' }}>•</td>
                      {isHistoryColVisible('order') && (
                        <td style={{ textAlign: 'center' }}>
                          <span className="badge badge-secondary" style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            #{row.order}
                          </span>
                        </td>
                      )}
                      {isHistoryColVisible('date') && (
                        <td>
                          <span style={{ fontWeight: 500 }}>{formatDateTime(row.createdAt)}</span>
                        </td>
                      )}
                      {isHistoryColVisible('cost') && (
                        <td className="tabular-nums" style={{ textAlign: 'right' }}>
                          <strong style={{ fontSize: '0.95rem' }}>₺{formatCurrency(row.cost)}</strong>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '4px' }}>/ {rmUnit}</span>
                        </td>
                      )}
                      {isHistoryColVisible('prev_cost') && (
                        <td className="tabular-nums" style={{ textAlign: 'right' }}>
                          {row.prevCost !== null ? (
                            <>
                              <span>₺{formatCurrency(row.prevCost)}</span>
                              <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '4px' }}>/ {rmUnit}</span>
                            </>
                          ) : (
                            <span className="text-muted">— (İlk Kayıt)</span>
                          )}
                        </td>
                      )}
                      {isHistoryColVisible('diff') && (
                        <td className="tabular-nums" style={{ textAlign: 'right' }}>
                          {row.diff === null ? (
                            <span className="text-muted">—</span>
                          ) : row.diff > 0 ? (
                            <span
                              className="badge"
                              style={{
                                backgroundColor: '#fee2e2',
                                color: '#b91c1c',
                                fontWeight: 600,
                                fontSize: '0.82rem',
                              }}
                            >
                              +₺{formatCurrency(row.diff)}
                            </span>
                          ) : row.diff < 0 ? (
                            <span
                              className="badge"
                              style={{
                                backgroundColor: '#d1fae5',
                                color: '#047857',
                                fontWeight: 600,
                                fontSize: '0.82rem',
                              }}
                            >
                              -₺{formatCurrency(Math.abs(row.diff))}
                            </span>
                          ) : (
                            <span className="badge badge-secondary">₺0,00</span>
                          )}
                        </td>
                      )}
                      {isHistoryColVisible('percent') && (
                        <td className="tabular-nums" style={{ textAlign: 'right' }}>
                          {row.percent === null ? (
                            <span className="text-muted">—</span>
                          ) : row.percent > 0 ? (
                            <strong style={{ color: '#dc2626' }}>
                              +%{formatNumber(row.percent, 2)}
                            </strong>
                          ) : row.percent < 0 ? (
                            <strong style={{ color: '#16a34a' }}>
                              -%{formatNumber(Math.abs(row.percent), 2)}
                            </strong>
                          ) : (
                            <span className="text-muted">%0,00</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Column Visibility Modal */}
      {showColumnModal && (
        <div className="modal-backdrop" onClick={() => setShowColumnModal(false)}>
          <div
            className="modal-card"
            style={{ maxWidth: '440px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem' }}>Sütun Görünürlüğü</h3>
              <button
                type="button"
                className="btn-icon"
                onClick={() => setShowColumnModal(false)}
                aria-label="Kapat"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  lineHeight: 1,
                  color: 'var(--text-muted, #6b7280)',
                  padding: '4px',
                }}
              >
                ✕
              </button>
            </div>
            <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              Hammadde raporu tablosunda görüntülemek istediğiniz sütunları işaretleyin.
            </p>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleShowAllColumns}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
              >
                Tümünü Seç
              </button>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleHideAllColumns}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
              >
                Tümünü Kaldır
              </button>
            </div>

            <div
              style={{
                maxHeight: '340px',
                overflowY: 'auto',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '0.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}
            >
              {columns.map((col) => {
                const checked = isColVisible(col.key)
                return (
                  <label
                    key={col.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '0.45rem 0.5rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      backgroundColor: checked ? '#f3f4f6' : 'transparent',
                      fontSize: '0.85rem',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggleColumn(col.key)}
                    />
                    <span>{col.label}</span>
                  </label>
                )
              })}
            </div>

            <div className="modal-actions" style={{ marginTop: '1rem', paddingTop: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowColumnModal(false)}
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Column Visibility Modal */}
      {showHistoryColumnModal && (
        <div className="modal-backdrop" onClick={() => setShowHistoryColumnModal(false)}>
          <div
            className="modal-card"
            style={{ maxWidth: '440px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem' }}>Sütun Görünürlüğü</h3>
              <button
                type="button"
                className="btn-icon"
                onClick={() => setShowHistoryColumnModal(false)}
                aria-label="Kapat"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  lineHeight: 1,
                  color: 'var(--text-muted, #6b7280)',
                  padding: '4px',
                }}
              >
                ✕
              </button>
            </div>
            <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              Fiyat revizyonları tablosunda görüntülemek istediğiniz sütunları işaretleyin.
            </p>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleShowAllHistoryColumns}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
              >
                Tümünü Seç
              </button>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleHideAllHistoryColumns}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
              >
                Tümünü Kaldır
              </button>
            </div>

            <div
              style={{
                maxHeight: '340px',
                overflowY: 'auto',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '0.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}
            >
              {historyColumns.map((col) => {
                const checked = isHistoryColVisible(col.key)
                return (
                  <label
                    key={col.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '0.45rem 0.5rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      backgroundColor: checked ? '#f3f4f6' : 'transparent',
                      fontSize: '0.85rem',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggleHistoryColumn(col.key)}
                    />
                    <span>{col.label}</span>
                  </label>
                )
              })}
            </div>

            <div className="modal-actions" style={{ marginTop: '1rem', paddingTop: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowHistoryColumnModal(false)}
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
